/**
 * Which locale the module is rendering in, and which way its text runs.
 *
 * The module shipped as single-locale en-US LTR by NFR A2, with the locale baked
 * into the formatters as literals — a `$` in `formatPrice`, English month names
 * in `formatUpdatedAt`. That is a reasonable v1 and an expensive v2: every one of
 * those literals is a separate site to find later. This file makes the locale a
 * *value* the formatters read, so adding a second locale is data rather than a
 * hunt through the render tree.
 *
 * WHERE THE LOCALE COMES FROM
 * ---------------------------
 * In priority order, first answer wins:
 *
 *   1. an explicit override (`setLocale`) — how the host app or a test pins it;
 *   2. `<html lang>` — the application's own declaration, and the one the
 *      browser already uses for hyphenation, spell-check and `:lang()`;
 *   3. `navigator.language` — the user's stated preference;
 *   4. `en-US` — the documented default.
 *
 * `<html lang>` sits above `navigator.language` deliberately. If the app has
 * decided it is rendering in one language, formatting numbers in another is
 * worse than either — it produces a page that reads in English and prices in
 * German, which is the failure people actually report.
 */

/** The documented fallback when nothing else answers. */
export const DEFAULT_LOCALE = 'en-US';

/**
 * Languages written right-to-left, as a fallback for engines without
 * `Intl.Locale#textInfo` (Safari only gained it in 17). Language subtags, not
 * scripts: an explicit `-Arab`/`-Hebr` script subtag is handled separately below,
 * which is what makes `az-Arab` come out RTL while plain `az` stays LTR.
 */
const RTL_LANGUAGES = new Set([
  'ar', // Arabic
  'arc', // Aramaic
  'ckb', // Central Kurdish
  'dv', // Divehi
  'fa', // Persian
  'he', // Hebrew
  'khw', // Khowar
  'ks', // Kashmiri
  'nqo', // N'Ko
  'ps', // Pashto
  'sd', // Sindhi
  'ug', // Uyghur
  'ur', // Urdu
  'yi', // Yiddish
]);

const RTL_SCRIPTS = new Set(['Arab', 'Hebr', 'Syrc', 'Thaa', 'Nkoo', 'Adlm']);

let override: string | null = null;

/**
 * Pins the locale for everything below. The host application calls this once;
 * tests call it to assert a specific locale's output.
 *
 * Passing `null` returns to detection — which is what a test's cleanup should do
 * rather than pinning it back to en-US, so a leaked override cannot masquerade
 * as the default.
 */
export const setLocale = (locale: string | null): void => {
  override = locale;
};

const documentLocale = (): string | null => {
  if (typeof document === 'undefined') {
    return null;
  }
  const lang = document.documentElement.getAttribute('lang');
  return lang && lang.trim() !== '' ? lang.trim() : null;
};

const navigatorLocale = (): string | null => {
  if (typeof navigator === 'undefined') {
    return null;
  }
  const candidate = navigator.language;
  return candidate && candidate.trim() !== '' ? candidate.trim() : null;
};

/**
 * A locale tag `Intl` will accept, always. Detection can hand back anything an
 * environment cares to put in `lang=` — every formatter here would throw a
 * `RangeError` on a malformed tag, and a broken language attribute must not take
 * a page down.
 */
const usable = (candidate: string | null): string | null => {
  if (candidate === null) {
    return null;
  }
  try {
    // The narrowest check that actually exercises the parser.
    Intl.NumberFormat.supportedLocalesOf([candidate]);
    return candidate;
  } catch {
    return null;
  }
};

export const resolveLocale = (): string =>
  usable(override) ?? usable(documentLocale()) ?? usable(navigatorLocale()) ?? DEFAULT_LOCALE;

export type Direction = 'ltr' | 'rtl';

/**
 * Which way the locale's text runs.
 *
 * `Intl.Locale#textInfo` is the correct answer and is not everywhere yet, so a
 * subtag check backs it up. Both are tried before defaulting to `ltr`, and the
 * default is `ltr` rather than a throw: an unknown locale rendering
 * left-to-right is a cosmetic problem, and an exception in a formatter is a
 * blank page.
 */
export const resolveDirection = (locale: string = resolveLocale()): Direction => {
  try {
    const parsed = new Intl.Locale(locale);

    // Named `textInfo` in the shipped spec; `getTextInfo()` in earlier drafts.
    // Neither is in this TS lib's type surface, hence the narrow cast.
    const withText = parsed as unknown as {
      textInfo?: { direction?: string };
      getTextInfo?: () => { direction?: string };
    };
    const direction = withText.textInfo?.direction ?? withText.getTextInfo?.().direction;
    if (direction === 'rtl' || direction === 'ltr') {
      return direction;
    }

    if (parsed.script && RTL_SCRIPTS.has(parsed.script)) {
      return 'rtl';
    }
    if (parsed.language && RTL_LANGUAGES.has(parsed.language)) {
      return 'rtl';
    }
  } catch {
    // Fall through to the subtag check on a tag Intl.Locale would not parse.
  }

  const language = locale.toLowerCase().split(/[-_]/)[0];
  return RTL_LANGUAGES.has(language) ? 'rtl' : 'ltr';
};

export const isRtl = (locale: string = resolveLocale()): boolean =>
  resolveDirection(locale) === 'rtl';
